import { trpc, trpcRetry } from "@carrot/bff-client"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"

// Derived from the client so the consent identifiers can't drift from the server
// contract — passing an unknown consent fails to compile. `.mutate`'s input is typed
// `Input | void`, so exclude the void before indexing.
type SignConsentInput = Exclude<Parameters<typeof trpc["telemedicine"]["signConsent"]["mutate"]>[0], void>
export type AcknowledgedConsents = SignConsentInput["acknowledgedConsents"]

const BOOKING_CONTEXT_QUERY_KEY = "telemedicine.getBookingContext"

// The booking flow's initial-load bundle: the member's legal name (for the confirmation screen)
// and their consent state (the consent-gate decision), fetched once when the flow mounts.
export function useBookingContext() {
  return useQuery({
    queryKey: [BOOKING_CONTEXT_QUERY_KEY],
    queryFn: () => trpc.telemedicine.getBookingContext.query(),
    refetchOnWindowFocus: false,
    retry: trpcRetry(1)
  })
}

export function useSignConsent() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (acknowledgedConsents: AcknowledgedConsents) =>
      trpc.telemedicine.signConsent.mutate({ acknowledgedConsents }),
    // Consent state rides in the booking-context bundle now — invalidate it so a remount of the
    // flow reflects the just-signed state instead of gating on the pre-sign value.
    onSuccess: () => queryClient.invalidateQueries({ queryKey: [BOOKING_CONTEXT_QUERY_KEY] })
  })
}

// ---- Booking-flow screening (OPS-9280) ----

// Types derived from the client so they can't drift from the server contract.
type GetScreeningQuestionsInput = Exclude<
  Parameters<typeof trpc["telemedicine"]["getScreeningQuestions"]["query"]>[0],
  void
>
export type ScreeningProgram = GetScreeningQuestionsInput["program"]
export type ScreeningAppointmentType = GetScreeningQuestionsInput["appointmentType"]

type SubmitScreeningInput = Exclude<Parameters<typeof trpc["telemedicine"]["submitScreening"]["mutate"]>[0], void>

const SCREENING_QUESTIONS_QUERY_KEY = "telemedicine.getScreeningQuestions"

export function useScreeningQuestions(program: ScreeningProgram, appointmentType: ScreeningAppointmentType) {
  return useQuery({
    queryKey: [SCREENING_QUESTIONS_QUERY_KEY, program, appointmentType],
    queryFn: () => trpc.telemedicine.getScreeningQuestions.query({ program, appointmentType }),
    refetchOnWindowFocus: false,
    retry: trpcRetry(1)
  })
}

export function useSubmitScreening() {
  return useMutation({
    mutationFn: (input: SubmitScreeningInput) => trpc.telemedicine.submitScreening.mutate(input)
  })
}

// ---- Booking-flow eligibility gate (MEM-760) ----

export type BookingEligibility = Awaited<ReturnType<typeof trpc["telemedicine"]["getBookingEligibility"]["query"]>>

const BOOKING_ELIGIBILITY_QUERY_KEY = "telemedicine.getBookingEligibility"

export function useBookingEligibility(program: ScreeningProgram) {
  return useQuery({
    queryKey: [BOOKING_ELIGIBILITY_QUERY_KEY, program],
    queryFn: () => trpc.telemedicine.getBookingEligibility.query({ program }),
    refetchOnWindowFocus: false,
    retry: trpcRetry(1)
  })
}

// ---- Booking-flow visit preferences (MEM-713) ----

// Types derived from the client so they can't drift from the server contract.
export type BookingOffering = Awaited<ReturnType<typeof trpc["telemedicine"]["getBookingOffering"]["query"]>>
export type Offering = BookingOffering["offerings"][number]

const BOOKING_OFFERING_QUERY_KEY = "telemedicine.getBookingOffering"

export function useBookingOffering(program: ScreeningProgram) {
  return useQuery({
    queryKey: [BOOKING_OFFERING_QUERY_KEY, program],
    queryFn: () => trpc.telemedicine.getBookingOffering.query({ program }),
    refetchOnWindowFocus: false,
    retry: trpcRetry(1)
  })
}

// ---- Booking-flow availability (MEM-716) ----

// Types derived from the client so they can't drift from the server contract. A slot
// mirrors the scheduling-api `Slot` wire model (UTC times + slotToken + host ids).
type GetAvailabilityInput = Exclude<Parameters<typeof trpc["telemedicine"]["getAvailability"]["query"]>[0], void>
// `outcome` reaches the UI unused for now: only `slots_available` may render times, and every other
// outcome shares one empty state. Distinguishing them for the member is designed, not built.
export type AvailabilityResult = Awaited<ReturnType<typeof trpc["telemedicine"]["getAvailability"]["query"]>>
export type AvailabilitySlot = AvailabilityResult["slots"][number]

const AVAILABILITY_QUERY_KEY = "telemedicine.getAvailability"

// Bookable slots for a visit type + location over a date window. Disabled until the caller
// has both a visit type and a location (the availability query's required inputs).
export function useAvailability(input: GetAvailabilityInput, options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: [
      AVAILABILITY_QUERY_KEY,
      input.program,
      input.offeringId,
      input.stateCode,
      input.language,
      input.from,
      input.to
    ],
    queryFn: () => trpc.telemedicine.getAvailability.query(input),
    enabled: options?.enabled ?? true,
    refetchOnWindowFocus: false,
    // Availability changes slowly relative to a booking session; avoid refetching the full
    // window on every remount (e.g. toggling preferences back and forth).
    staleTime: 60_000,
    retry: trpcRetry(1)
  })
}

// ---- Booking-flow booking (MEM-719) ----

// Types derived from the client so they can't drift from the server contract.
type BookAppointmentInput = Exclude<Parameters<typeof trpc["telemedicine"]["bookAppointment"]["mutate"]>[0], void>
// The booked appointment the confirmation screen renders (assigned provider + UTC visit window).
export type BookedAppointment = Awaited<ReturnType<typeof trpc["telemedicine"]["bookAppointment"]["mutate"]>>

// Redeems the chosen slot and creates the appointment. Not retried: booking is a write, and a
// failure keeps the member on the time picker to retry deliberately.
export function useBookAppointment() {
  return useMutation({
    mutationFn: (input: BookAppointmentInput) => trpc.telemedicine.bookAppointment.mutate(input)
  })
}

// ---- Telemedicine entry / dispatch (MEM-677) ----

// The resolver's dispatch decision (from the BFF contract). "single" names the program; "multiple"
// doesn't enumerate, so the client never learns the member's full entitlement set.
export type TelemedicineEntry = Awaited<ReturnType<typeof trpc["telemedicine"]["getTelemedicineEntry"]["query"]>>

const TELEMEDICINE_ENTRY_QUERY_KEY = "telemedicine.getTelemedicineEntry"

export function useTelemedicineEntry() {
  return useQuery({
    queryKey: [TELEMEDICINE_ENTRY_QUERY_KEY],
    queryFn: () => trpc.telemedicine.getTelemedicineEntry.query(),
    refetchOnWindowFocus: false,
    retry: trpcRetry(1)
  })
}

// ---- Per-program access (MEM-677) ----

// Whether the member may book a program (from the BFF contract). "denied" is uniform; "allowed"
// echoes the canonical program.
export type ProgramAccess = Awaited<ReturnType<typeof trpc["telemedicine"]["getProgramAccess"]["query"]>>

const PROGRAM_ACCESS_QUERY_KEY = "telemedicine.getProgramAccess"

// Disabled when there's no program (the page routes that case away first), so it never hits the BFF.
export function useProgramAccess(program: string | null) {
  return useQuery({
    queryKey: [PROGRAM_ACCESS_QUERY_KEY, program],
    queryFn: () => trpc.telemedicine.getProgramAccess.query({ program: program ?? "" }),
    enabled: program !== null,
    refetchOnWindowFocus: false,
    retry: trpcRetry(1)
  })
}
